// src/components/PredictionsView.tsx

import React, { useEffect } from 'react'
import { Col, Row } from 'react-bootstrap'
import { Prediction } from '../../types/prediction'
import { selectSelectedModelId } from '../../store/slices/modelSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { clearAllBoundingBoxes, setSelectedContentId } from '../../store/slices/annotationSlice'
import { setEditingPrediction } from '../../store/slices/predictionSlice'
import {
  addSelectedPrediction,
  removeSelectedPrediction,
  selectActiveConfidenceLevel,
} from '../../store/slices/predictionSlice'
import { RootState } from '../../store'
import PredictionImage from '../PredictionImage'
import EmptyPredictionImage from '../EmptyPredictionImage'
import { useGetModelQuery } from '../../services/modelsApi'
import { resetImageLoadingQueue } from '../../hooks/useContentImage'

import GalleryScaffold from '../gallery/Scaffold'
import GalleryGrid from '../gallery/Grid'
import GalleryTile from '../gallery/Tile'

interface PredictionsGalleryViewProps {
  filteredPredictions: Prediction[]
  arePredictionsLoading: boolean
  page?: number
  imagesPerRow: number
  itemsPerPage?: number
  bboxThreshold: number
  maxConcurrentRequests?: number
}

const PredictionsGalleryView: React.FC<PredictionsGalleryViewProps> = ({
  filteredPredictions,
  arePredictionsLoading,
  imagesPerRow,
  bboxThreshold,
  maxConcurrentRequests = 10,
}) => {
  const dispatch = useAppDispatch()
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })
  const selectedPredictions = useAppSelector(
    (state: RootState) => state.prediction.selectedPredictions
  )
  const activeConfidenceLevel = useAppSelector(selectActiveConfidenceLevel)

  useEffect(() => {
    resetImageLoadingQueue(maxConcurrentRequests)
  }, [maxConcurrentRequests])

  const handleGridPredictionClick = (prediction: Prediction) => {
    if (prediction) {
      dispatch(setEditingPrediction(prediction))
      dispatch(setSelectedContentId(prediction.contentid))
      dispatch(clearAllBoundingBoxes())
    }
  }

  const handleTogglePrediction = (prediction: Prediction) => {
    if (selectedPredictions.some((p) => p.id === prediction.id)) {
      dispatch(removeSelectedPrediction(prediction.id))
    } else {
      dispatch(addSelectedPrediction(prediction))
    }
  }

  return (
    <>
      {selectedModel?.batch?.status?.toLowerCase().startsWith('complete') && (
        <Row className="h-100">
          <Col className="h-100">
            <GalleryScaffold
              isLoading={arePredictionsLoading}
              empty={!filteredPredictions?.length}
              emptyMessage="No new suggestive labels available."
            >
              <GalleryGrid imagesPerRow={imagesPerRow}>
                {filteredPredictions.map((prediction: Prediction) => (
                  <GalleryTile key={prediction.id}>
                    {activeConfidenceLevel === 'none' ? (
                      <EmptyPredictionImage
                        prediction={prediction}
                        onEdit={handleGridPredictionClick}
                        onToggle={handleTogglePrediction}
                        isSelected={selectedPredictions?.some((p) => p.id === prediction.id)}
                      />
                    ) : (
                      <PredictionImage
                        prediction={prediction}
                        onEdit={handleGridPredictionClick}
                        onToggle={handleTogglePrediction}
                        isSelected={selectedPredictions?.some((p) => p.id === prediction.id)}
                        bboxThreshold={bboxThreshold}
                      />
                    )}
                  </GalleryTile>
                ))}
              </GalleryGrid>
            </GalleryScaffold>
          </Col>
        </Row>
      )}
    </>
  )
}

export default PredictionsGalleryView
